import { Container, Graphics } from "pixi.js";
import { createFeatheredEllipse } from "../lib/soft-shape";
import { safePixiColor } from "../lib/token-render-state";
import { windStrength } from "../lib/wind";
import type { Point, Token, TreeDensity, TreeSpecies, WindSpeed } from "../types";

export interface ProceduralTreeResult {
  base: Container;
  foliage: Container;
  animate: (deltaMs: number) => void;
}

function seedFrom(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index); hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function randomGenerator(seed: number) {
  let value = seed || 1;
  return () => {
    value += 0x6d2b79f5;
    let result = value; result = Math.imul(result ^ result >>> 15, result | 1);
    result ^= result + Math.imul(result ^ result >>> 7, result | 61);
    return ((result ^ result >>> 14) >>> 0) / 4294967296;
  };
}

function safeHex(value: string): string {
  return safePixiColor(value);
}

function shade(value: string, amount: number): string {
  const hex = safeHex(value).slice(1); const number = Number.parseInt(hex, 16);
  const channel = (shift: number) => Math.max(0, Math.min(255, ((number >> shift) & 255) + amount));
  return `#${[channel(16), channel(8), channel(0)].map((item) => Math.round(item).toString(16).padStart(2, "0")).join("")}`;
}

// TurboTwig's default broadleaf gets its realism from thousands of small,
// clustered leaf quads rather than a few oversized blobs. These counts keep
// that visual density while batching the static leaves into five Graphics
// layers so several tabletop trees remain practical.
const broadleafLeafCount: Record<TreeDensity, number> = { sparse: 680, normal: 1_480, dense: 2_520 };
const coniferNeedleCount: Record<TreeDensity, number> = { sparse: 390, normal: 760, dense: 1_260 };
const silhouetteCount: Record<TreeDensity, number> = { sparse: 42, normal: 76, dense: 118 };

interface FlutterLeaf {
  graphic: Graphics;
  x: number;
  y: number;
  rotation: number;
  phase: number;
}

interface AnimatedCanopy {
  layers: Container[];
  flutter: FlutterLeaf[];
}

interface CanopyCluster {
  x: number;
  y: number;
  radius: number;
}

interface BranchCurve {
  start: Point;
  control1: Point;
  control2: Point;
  end: Point;
  startWidth: number;
  endWidth: number;
}

function cubicPoint(curve: BranchCurve, amount: number): Point {
  const inverse = 1 - amount;
  const inverseSquared = inverse * inverse;
  const amountSquared = amount * amount;
  return {
    x: inverseSquared * inverse * curve.start.x + 3 * inverseSquared * amount * curve.control1.x + 3 * inverse * amountSquared * curve.control2.x + amountSquared * amount * curve.end.x,
    y: inverseSquared * inverse * curve.start.y + 3 * inverseSquared * amount * curve.control1.y + 3 * inverse * amountSquared * curve.control2.y + amountSquared * amount * curve.end.y,
  };
}

function cubicTangent(curve: BranchCurve, amount: number): Point {
  const inverse = 1 - amount;
  return {
    x: 3 * inverse * inverse * (curve.control1.x - curve.start.x) + 6 * inverse * amount * (curve.control2.x - curve.control1.x) + 3 * amount * amount * (curve.end.x - curve.control2.x),
    y: 3 * inverse * inverse * (curve.control1.y - curve.start.y) + 6 * inverse * amount * (curve.control2.y - curve.control1.y) + 3 * amount * amount * (curve.end.y - curve.control2.y),
  };
}

function curveWidth(curve: BranchCurve, amount: number, multiplier = 1): number {
  // TurboTwig tapers more rapidly near the twig while retaining a subtle
  // contact flare at the junction. The eased curve avoids bar-like limbs.
  const tapered = Math.pow(amount, 1.35);
  return (curve.startWidth + (curve.endWidth - curve.startWidth) * tapered) * multiplier;
}

function drawTaperedCurve(graphics: Graphics, curve: BranchCurve, color: string, alpha: number, widthMultiplier = 1) {
  const left: Point[] = []; const right: Point[] = []; const samples = 8;
  for (let index = 0; index < samples; index += 1) {
    const amount = index / (samples - 1); const point = cubicPoint(curve, amount); const tangent = cubicTangent(curve, amount);
    const magnitude = Math.max(0.001, Math.hypot(tangent.x, tangent.y)); const halfWidth = curveWidth(curve, amount, widthMultiplier) * 0.5;
    const normalX = -tangent.y / magnitude; const normalY = tangent.x / magnitude;
    left.push({ x: point.x + normalX * halfWidth, y: point.y + normalY * halfWidth });
    right.push({ x: point.x - normalX * halfWidth, y: point.y - normalY * halfWidth });
  }
  graphics.moveTo(left[0].x, left[0].y);
  for (let index = 1; index < left.length; index += 1) graphics.lineTo(left[index].x, left[index].y);
  for (let index = right.length - 1; index >= 0; index -= 1) graphics.lineTo(right[index].x, right[index].y);
  graphics.closePath().fill({ color, alpha });
  const tip = cubicPoint(curve, 1); graphics.circle(tip.x, tip.y, Math.max(0.3, curve.endWidth * widthMultiplier * 0.5)).fill({ color, alpha });
}

function drawBarkRidge(graphics: Graphics, curve: BranchCurve, color: string, width: number, alpha: number, offset: number) {
  const start = cubicPoint(curve, 0.08); const end = cubicPoint(curve, 0.88);
  const tangent = cubicTangent(curve, 0.45); const magnitude = Math.max(0.001, Math.hypot(tangent.x, tangent.y));
  const normal = { x: -tangent.y / magnitude * offset, y: tangent.x / magnitude * offset };
  graphics.moveTo(start.x + normal.x, start.y + normal.y)
    .bezierCurveTo(curve.control1.x + normal.x, curve.control1.y + normal.y, curve.control2.x + normal.x, curve.control2.y + normal.y, end.x + normal.x, end.y + normal.y)
    .stroke({ color, width, alpha, cap: "round", join: "round" });
}

function createBranchCurve(start: Point, angle: number, length: number, flatten: number, startWidth: number, endWidth: number, bend: number, random: () => number): BranchCurve {
  const direction = { x: Math.cos(angle), y: Math.sin(angle) * flatten };
  const perpendicular = { x: -Math.sin(angle), y: Math.cos(angle) * flatten };
  const jitter = (random() - 0.5) * length * 0.055;
  return {
    start,
    control1: {
      x: start.x + direction.x * length * 0.30 + perpendicular.x * bend * 0.34,
      y: start.y + direction.y * length * 0.30 + perpendicular.y * bend * 0.34,
    },
    control2: {
      x: start.x + direction.x * length * 0.68 + perpendicular.x * bend * 0.82 + perpendicular.x * jitter,
      y: start.y + direction.y * length * 0.68 + perpendicular.y * bend * 0.82 + perpendicular.y * jitter,
    },
    end: {
      x: start.x + direction.x * length + perpendicular.x * bend,
      y: start.y + direction.y * length + perpendicular.y * bend,
    },
    startWidth,
    endWidth,
  };
}

function drawOrganicBranches(base: Container, radius: number, species: TreeSpecies, random: () => number) {
  const branchShadow = new Graphics(); const branchBark = new Graphics(); const branchInner = new Graphics(); const barkRidges = new Graphics();
  const mainCount = species === "conifer" ? 8 : 7; const flatten = species === "conifer" ? 0.82 : 0.78; const curves: BranchCurve[] = [];

  for (let index = 0; index < mainCount; index += 1) {
    const angle = index / mainCount * Math.PI * 2 + (random() - 0.5) * 0.34;
    const length = radius * (species === "conifer" ? 0.33 + random() * 0.17 : 0.34 + random() * 0.20);
    const main = createBranchCurve(
      { x: (random() - 0.5) * radius * 0.035, y: (random() - 0.5) * radius * 0.028 }, angle, length, flatten,
      radius * (species === "conifer" ? 0.078 : 0.090) * (0.82 + random() * 0.24), radius * (0.008 + random() * 0.007),
      (random() - 0.5) * radius * 0.25, random,
    );
    curves.push(main);

    const forkCount = species === "conifer" ? 1 : (random() > 0.52 ? 2 : 1);
    for (let forkIndex = 0; forkIndex < forkCount; forkIndex += 1) {
      const joinAmount = 0.48 + forkIndex * 0.15 + random() * 0.07; const join = cubicPoint(main, joinAmount);
      const side = forkIndex % 2 === 0 ? -1 : 1; const forkAngle = angle + side * (0.34 + random() * 0.34);
      const forkLength = radius * (0.10 + random() * 0.13) * (1 - forkIndex * 0.12);
      curves.push(createBranchCurve(
        join, forkAngle, forkLength, flatten, curveWidth(main, joinAmount) * 0.62, radius * (0.005 + random() * 0.006),
        (random() - 0.5) * radius * 0.10, random,
      ));
    }
  }

  // Short root flares break up the circular base and weld the limbs visually
  // into the trunk, echoing TurboTwig without exposing more tree controls.
  for (let index = 0; index < 5; index += 1) {
    const angle = index / 5 * Math.PI * 2 + (random() - 0.5) * 0.38;
    curves.unshift(createBranchCurve(
      { x: 0, y: 0 }, angle, radius * (0.14 + random() * 0.08), 0.76,
      radius * (0.10 + random() * 0.018), radius * 0.008, (random() - 0.5) * radius * 0.06, random,
    ));
  }

  for (const curve of curves) drawTaperedCurve(branchShadow, { ...curve, start: { x: curve.start.x + radius * 0.014, y: curve.start.y + radius * 0.019 }, control1: { x: curve.control1.x + radius * 0.014, y: curve.control1.y + radius * 0.019 }, control2: { x: curve.control2.x + radius * 0.014, y: curve.control2.y + radius * 0.019 }, end: { x: curve.end.x + radius * 0.014, y: curve.end.y + radius * 0.019 } }, "#17100b", 0.20, 1.10);
  for (const curve of curves) drawTaperedCurve(branchBark, curve, "#2f2118", 0.82, 1.07);
  for (const curve of curves) drawTaperedCurve(branchInner, curve, species === "conifer" ? "#49331f" : "#503724", 0.84, 0.76);
  for (const curve of curves) {
    drawBarkRidge(barkRidges, curve, "#8c6544", Math.max(0.32, curve.endWidth * 0.21), 0.23, -curve.startWidth * 0.12);
    if (curve.startWidth > radius * 0.06) drawBarkRidge(barkRidges, curve, "#211711", Math.max(0.28, curve.endWidth * 0.16), 0.21, curve.startWidth * 0.15);
  }
  base.addChild(branchShadow, branchBark, branchInner, barkRidges);
}

function leafColor(base: string, random: () => number, layer: number, lightBias = 0): string {
  const roll = random();
  if (roll < 0.10) return shade(base, 18 - layer * 2 + lightBias);
  if (roll < 0.34) return shade(base, 7 - layer * 2 + lightBias);
  if (roll > 0.88) return shade(base, -28 + layer * 2 + lightBias);
  if (roll > 0.64) return shade(base, -15 + layer * 2 + lightBias);
  return shade(base, -4 + layer + lightBias);
}

function drawLeaf(graphics: Graphics, x: number, y: number, length: number, width: number, rotation: number, color: string, alpha = 0.96) {
  const directionX = Math.cos(rotation); const directionY = Math.sin(rotation); const sideX = -directionY; const sideY = directionX;
  const half = length * 0.5; const startX = x - directionX * half; const startY = y - directionY * half; const tipX = x + directionX * half; const tipY = y + directionY * half;
  const shoulderX = x - directionX * length * 0.06; const shoulderY = y - directionY * length * 0.06;
  graphics.moveTo(startX, startY)
    .lineTo(shoulderX + sideX * width, shoulderY + sideY * width)
    .lineTo(tipX, tipY)
    .lineTo(shoulderX - sideX * width, shoulderY - sideY * width)
    .closePath().fill({ color, alpha });
}

function addFlutterLeaves(canopy: Container, radius: number, color: string, count: number, random: () => number, conifer = false): FlutterLeaf[] {
  const flutter: FlutterLeaf[] = [];
  for (let index = 0; index < count; index += 1) {
    const angle = random() * Math.PI * 2; const distance = radius * (0.28 + random() * 0.58); const x = Math.cos(angle) * distance; const y = Math.sin(angle) * distance * 0.78;
    const graphic = new Graphics(); const rotation = conifer ? angle + (random() - 0.5) * 0.6 : random() * Math.PI;
    const length = radius * (conifer ? 0.026 + random() * 0.015 : 0.024 + random() * 0.018);
    drawLeaf(graphic, 0, 0, length, length * (conifer ? 0.11 : 0.23), 0, shade(color, 24 + random() * 13), 0.90);
    graphic.position.set(x, y); graphic.rotation = rotation; canopy.addChild(graphic);
    flutter.push({ graphic, x, y, rotation, phase: random() * Math.PI * 2 });
  }
  return flutter;
}

function broadleafClusters(radius: number, density: TreeDensity, random: () => number): CanopyCluster[] {
  const outerCount = density === "sparse" ? 9 : density === "dense" ? 19 : 14;
  const clusters: CanopyCluster[] = [
    { x: -radius * 0.15, y: -radius * 0.09, radius: radius * 0.30 },
    { x: radius * 0.13, y: -radius * 0.03, radius: radius * 0.28 },
    { x: -radius * 0.02, y: radius * 0.15, radius: radius * 0.26 },
  ];
  for (let index = 0; index < outerCount; index += 1) {
    const angle = index / outerCount * Math.PI * 2 + (random() - 0.5) * 0.48;
    const distance = radius * (0.35 + random() * 0.27);
    clusters.push({
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance * 0.82 + Math.max(0, Math.sin(angle)) * radius * 0.035,
      radius: radius * (0.18 + random() * 0.12),
    });
  }
  return clusters;
}

function clusteredPoint(cluster: CanopyCluster, random: () => number, spread = 1): Point {
  const angle = random() * Math.PI * 2; const distance = Math.sqrt(random()) * cluster.radius * spread;
  return { x: cluster.x + Math.cos(angle) * distance, y: cluster.y + Math.sin(angle) * distance * 0.82 };
}

function drawBroadleaf(canopy: Container, radius: number, density: TreeDensity, color: string, random: () => number): AnimatedCanopy {
  const silhouette = new Graphics(); const clusters = broadleafClusters(radius, density, random);
  for (const cluster of clusters) {
    silhouette.ellipse(cluster.x, cluster.y, cluster.radius * 0.94, cluster.radius * (0.70 + random() * 0.12)).fill({
      color: shade(color, -31 - random() * 8), alpha: density === "sparse" ? 0.20 : density === "dense" ? 0.34 : 0.28,
    });
  }
  for (let index = 0; index < silhouetteCount[density]; index += 1) {
    const cluster = clusters[Math.floor(random() * clusters.length)]; const point = clusteredPoint(cluster, random, 0.82); const size = radius * (0.035 + random() * 0.060);
    silhouette.ellipse(point.x, point.y, size, size * (0.62 + random() * 0.24)).fill({ color: index % 5 ? shade(color, -24) : shade(color, -38), alpha: 0.44 + random() * 0.25 });
  }
  canopy.addChild(silhouette);
  const layers = Array.from({ length: 5 }, () => new Container());
  const graphics = layers.map(() => new Graphics()); layers.forEach((layer, index) => { layer.addChild(graphics[index]); canopy.addChild(layer); });
  for (let index = 0; index < broadleafLeafCount[density]; index += 1) {
    const layer = index % layers.length; const cluster = clusters[Math.floor(random() * clusters.length)]; const point = clusteredPoint(cluster, random, 1.05);
    const length = radius * (0.018 + random() * 0.025); const width = length * (0.17 + random() * 0.13);
    const branchDirection = Math.atan2(cluster.y, cluster.x); const localDirection = Math.atan2(point.y - cluster.y, point.x - cluster.x);
    const rotation = branchDirection * 0.58 + localDirection * 0.42 + (random() - 0.5) * 1.05;
    const directionalLight = Math.round(((-point.x - point.y) / Math.max(radius, 1)) * 5);
    drawLeaf(graphics[layer], point.x, point.y, length, width, rotation, leafColor(color, random, layer, directionalLight), 0.83 + random() * 0.16);
  }
  const dapple = new Graphics();
  const dappleCount = density === "sparse" ? 30 : density === "dense" ? 82 : 54;
  for (let index = 0; index < dappleCount; index += 1) {
    const cluster = clusters[Math.floor(random() * clusters.length)]; const point = clusteredPoint(cluster, random, 0.72);
    dapple.ellipse(point.x - radius * 0.09, point.y - radius * 0.08, radius * (0.008 + random() * 0.015), radius * (0.003 + random() * 0.008)).fill({ color: shade(color, 38), alpha: 0.05 + random() * 0.08 });
  }
  layers[4].addChild(dapple);
  return { layers, flutter: addFlutterLeaves(canopy, radius, color, density === "dense" ? 96 : density === "normal" ? 64 : 38, random) };
}

function drawConifer(canopy: Container, radius: number, density: TreeDensity, color: string, random: () => number): AnimatedCanopy {
  const rings = density === "sparse" ? 4 : density === "dense" ? 7 : 5; const silhouette = new Graphics();
  for (let ring = rings; ring >= 1; ring -= 1) {
    const ringRadius = radius * (0.22 + ring / rings * 0.76); const points = density === "dense" ? 22 : 18;
    for (let index = 0; index < points * 2; index += 1) {
      const angle = index / (points * 2) * Math.PI * 2 - Math.PI / 2; const jagged = index % 2 ? 0.72 + random() * 0.09 : 0.96 + random() * 0.10;
      const x = Math.cos(angle) * ringRadius * jagged; const y = Math.sin(angle) * ringRadius * jagged * 0.86;
      index ? silhouette.lineTo(x, y) : silhouette.moveTo(x, y);
    }
    silhouette.closePath().fill({ color: ring % 2 ? shade(color, -25) : shade(color, -9), alpha: 0.88 });
  }
  canopy.addChild(silhouette);
  const layers = Array.from({ length: 4 }, () => new Container());
  const graphics = layers.map(() => new Graphics()); layers.forEach((layer, index) => { layer.addChild(graphics[index]); canopy.addChild(layer); });
  const needles = coniferNeedleCount[density];
  for (let index = 0; index < needles; index += 1) {
    const layer = index % layers.length; const angle = random() * Math.PI * 2; const distance = Math.sqrt(random()) * radius * 0.92; const x = Math.cos(angle) * distance; const y = Math.sin(angle) * distance * 0.83;
    const length = radius * (0.043 + random() * 0.047); drawLeaf(graphics[layer], x, y, length, length * (0.09 + random() * 0.06), angle + (random() - 0.5) * 0.72, leafColor(color, random, layer), 0.87 + random() * 0.11);
  }
  return { layers, flutter: addFlutterLeaves(canopy, radius, color, density === "dense" ? 52 : density === "normal" ? 34 : 20, random, true) };
}

/** A deliberately small top-down procedural tree renderer inspired by the TurboTwig experiment. */
export function createProceduralTree(token: Token, position: Point, radius: number, alpha: number, windSpeed: WindSpeed = "normal"): ProceduralTreeResult {
  const species: TreeSpecies = token.tree?.species ?? "broadleaf"; const density: TreeDensity = token.tree?.density ?? "normal";
  const random = randomGenerator(seedFrom(token.id)); const base = new Container(); const foliage = new Container();
  base.position.set(position.x, position.y); foliage.position.set(position.x, position.y); base.alpha = alpha; foliage.alpha = alpha;
  const shadow = createFeatheredEllipse({ x: radius * 0.16, y: radius * 0.22, radiusX: radius * 1.10, radiusY: radius * 0.82, color: "#070a06", alpha: 0.30, feather: 0.48 });
  base.addChild(shadow); drawOrganicBranches(base, radius, species, random);
  const trunk = new Graphics().circle(0, 0, radius * (species === "conifer" ? 0.145 : 0.17)).fill({ color: "#654127" }).stroke({ color: "#322116", width: Math.max(1, radius * 0.035) });
  const trunkRings = new Graphics();
  for (let ring = 0; ring < 3; ring += 1) trunkRings.circle(0, 0, radius * ((species === "conifer" ? 0.05 : 0.06) + ring * 0.038)).stroke({ color: ring % 2 ? "#8a6040" : "#40291b", width: Math.max(0.35, radius * 0.008), alpha: 0.48 });
  // Draw the expensive foliage geometry in neutral tones. Pixi can then tint
  // the complete canopy instantly without recreating thousands of leaf paths.
  const canopy = new Container(); const color = "#ffffff";
  const animated = species === "conifer" ? drawConifer(canopy, radius, density, color, random) : drawBroadleaf(canopy, radius, density, color, random);
  base.addChild(trunk, trunkRings); foliage.addChild(canopy); foliage.tint = safePixiColor(token.color);
  let elapsed = seedFrom(token.id) % 1000; const wind = windStrength(windSpeed); const tempo = 0.78 + wind * 0.22;
  const animate = (deltaMs: number) => {
    elapsed += Math.min(deltaMs, 80) / 1000 * tempo;
    const breeze = Math.sin(elapsed * 0.72) * radius * 0.018 * wind;
    canopy.position.set(breeze, Math.cos(elapsed * 0.51) * radius * 0.009 * wind); canopy.rotation = Math.sin(elapsed * 0.39) * 0.006 * wind;
    animated.layers.forEach((layer, index) => {
      const phase = index * 1.47; const gust = Math.sin(elapsed * (0.86 + index * 0.07) + phase);
      layer.position.set(gust * radius * (0.008 + index * 0.003) * wind, Math.cos(elapsed * 0.68 + phase) * radius * 0.004 * wind);
      layer.rotation = gust * (0.004 + index * 0.0025) * wind; const pulse = 1 + Math.sin(elapsed * 1.12 + phase) * 0.0035 * wind; layer.scale.set(pulse, 2 - pulse);
    });
    animated.flutter.forEach((leaf, index) => {
      const flutter = Math.sin(elapsed * (2.6 + index % 5 * 0.31) + leaf.phase); const lift = Math.cos(elapsed * (1.7 + index % 3 * 0.19) + leaf.phase);
      leaf.graphic.position.set(leaf.x + flutter * radius * 0.016 * wind, leaf.y + lift * radius * 0.009 * wind);
      leaf.graphic.rotation = leaf.rotation + flutter * 0.14 * wind; leaf.graphic.scale.set(1 + lift * 0.05 * wind, 1 - lift * 0.035 * wind);
    });
  };
  return { base, foliage, animate };
}
